import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import ListView from '../../list-view/jsx/ListView.jsx';
import MapKey from '../../map-key/jsx/MapKey.jsx';

import { GoogleMap, OverlayView, LoadScript } from '@react-google-maps/api';

import { getQueryParameterValuesByKey, filterArrayByProperty, sortByProperty, addRelativeDistanceToStationData, standardizeValue } from '../../redux/station-data/station-data.utils';

import { selectStationData } from '../../redux/station-data/station-data.selectors';
import { setFilteredStationData } from '../../redux/station-data/station-data.actions';

import { selectUserValidAddress, selectUserLat, selectUserLng, selectWindToggleIsActive, selectUniqueMapId } from '../../redux/user/user.selectors';
import { setWindToggleActive, setPollutantPopupType } from '../../redux/user/user.actions';

import { TICK, ARROW_COMPASS, ARROW_RIGHT } from '../../SVGs/svg.imports.js';

/**
 * Set these constants here so that they are
 * not re-calculated with each component render
 */
const googleMapContainerStyle = {
  "width": '100%',
  "height": '600px'
};

const googleMapRoadmapStyles = [
  {
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#F2F2F2"
      }
    ]
  },
  {
    "elementType": "labels.icon",
    "stylers": [
      {
        "visibility": "off"
      }
    ]
  },
  {
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#B8B8B8"
      }
    ]
  },
  {
    "elementType": "labels.text.stroke",
    "stylers": [
      {
        "color": "#ffffff"
      }
    ]
  },
  {
    "featureType": "administrative.province",
    "elementType": "geometry.stroke",
    "stylers": [
      {
        "color": "#a0a5ae"
      },
      {
        "visibility": "on"
      },
      {
        "weight": 1
      }
    ]
  },
  {
    "featureType": "administrative.land_parcel",
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#0000ff"
      }
    ]
  },
  {
    "featureType": "poi",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#eeeeee"
      }
    ]
  },
  {
    "featureType": "poi",
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#757575"
      }
    ]
  },
  {
    "featureType": "poi.park",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#EAEDEA"
      }
    ]
  },
  {
    "featureType": "poi.park",
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#B8B8B8"
      }
    ]
  },
  {
    "featureType": "road",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#ffffff"
      }
    ]
  },
  {
    "featureType": "road.arterial",
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#757575"
      }
    ]
  },
  {
    "featureType": "road.highway",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#ffffff"
      }
    ]
  },
  {
    "featureType": "road.highway",
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#616161"
      }
    ]
  },
  {
    "featureType": "road.local",
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#9e9e9e"
      }
    ]
  },
  {
    "featureType": "transit.line",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#e5e5e5"
      }
    ]
  },
  {
    "featureType": "transit.station",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#eeeeee"
      }
    ]
  },
  {
    "featureType": "water",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#DCE9EF"
      }
    ]
  },
  {
    "featureType": "water",
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#9e9e9e"
      }
    ]
  }
];

const googleMapHybridStyles = [
  {
    "featureType": "administrative.province",
    "elementType": "geometry.stroke",
    "stylers": [
      {
        "color": "#000000"
      },
      {
        "visibility": "on"
      },
      {
        "weight": 2
      }
    ]
  }
];

// See: https://developers.google.com/maps/documentation/javascript/reference/map#MapOptions
const googleMapOptions = {
  "styles": googleMapRoadmapStyles,
  "clickableIcons" : false,
  "disableDefaultUI": true,
  "fullscreenControl": false,
  "mapTypeControl": false,
  "mapTypeId": "roadmap",
  "minZoom": 5, // The minimum zoom level that is allowed for the map
  "restriction": {
    "latLngBounds": {
      "north": -20,
      "south": -48,
      "east": 172,
      "west": 115
    }
  },
  "streetViewControl": false,
  "zoomControl": true,
  "zoomControlOptions": {
    position: 3
  }
};

/**
 * The zoom level for when no address has
 * been entered (the initial zoom level)
 */
const googleMapDefaultZoom = 6;

/**
 * The minimum zoom level at which marker labels
 * should appear
 */
const googleMapLabelUnlockZoom = 9;

/**
 * The minimum zoom level at which the toggle
 * wind direction button and wind icons should appear
 */
const googleMapWindUnlockZoom = 9;

/**
 * The (preferred) minimum number of stations to
 * show on the map after the user enters an address
 */
const googleMapMinimumVisibleStations = 3;

/**
 * A flag for tracking if the user has had
 * any interaction with the map controls
 */
let userMapInteraction = false;

/**
 * A flag for tracking if we have updated the
 * map view to fit the stations
 */
let hasFitToBounds = false;

/**
 * The initial region to show in the map (optional)
 */
let regionName = null;

/**
 * A flag for tracking if we have displayed the
 * optional region stations in the map view
 */
let hasDisplayedRegion = true;

const MapView = ({ mapConfig, stationData, setFilteredStationDataFeed, userValidAddress, userLat, userLng, windToggleIsActive, setWindToggleActive, setPopupType, uniqueMapId, isHidden }) => {
  const mapRef = useRef(null);
  const [ mapLayerType, setMapLayerType ] = useState("roadmap");
  const [ currentZoomLevel, setCurrentZoomLevel ] = useState(googleMapLabelUnlockZoom);
  const [ stationPopupContentId, setStationPopupContentId ] = useState(null);
  // Set the initial map coordinates when this component mounts
  const [ mapCoords, setMapCoords ] = useState({
    "lat": "0",
    "lng": "0"
  });
  const [ sortedStationData, setSortedStationData ] = useState([]);
  const [ sortedMapPinData, setSortedMapPinData ] = useState([]);
  const loadingMessage = (<p className="station-map-view__loading-message">Loading interactive map...</p>);

  /**
   * When the map component first loads, check for any
   * query parameters that we use to update the map view
   */
  useEffect(() => {
    const queryString = window.location.href.split("?")[1] || "";
    regionName = getQueryParameterValuesByKey(queryString, 'region')[0] || null;

    if (regionName !== null) {
      // Set the name into a format we can filter by
      regionName = decodeURIComponent(regionName);
      /**
       * Trigger the map to show all stations from
       * within the provided region ID
       */
      hasDisplayedRegion = false;
    }
  }, []);

  /**
   * Reset the interactive map to reflect the user's
   * selected address.
   * Also conditionally trigger the map to zoom in on the
   * nearest station(s)
   */
  useEffect(() => {
    const originPoint = {
      "lat": 0,
      "lng": 0
    };

    /**
     * Close the station popup (if currently open) if
     * the map content has (or will) changed
     */
    setStationPopupContentId(null);

    // If the map position has changed since the last address search, reset the map
    if (mapCoords.lat !== userLat || mapCoords.lng !== userLng) {
      // Reset the flag for map interaction by the user
      userMapInteraction = false;

      // Reset the flag for fitting all the stations in the map
      hasFitToBounds = false;

      /**
       * Update the station data to match the user's location.
       * This will also trigger the map to re-center
       * on the nearest stations as well the map lat/lng
       * to be updated
       */
      originPoint.lat = parseFloat(userLat);
      originPoint.lng = parseFloat(userLng);

      if (stationData.length > 0) {
        // Capture any changes/updates to the station data
        setSortedStationData( addRelativeDistanceToStationData(originPoint, stationData) );
      }
    }
  }, [uniqueMapId, userLat, userLng]);

  /**
   * When local-state map data is updated, always update the
   * filtered data list to capture any changes.
   * Also conditionally trigger the map to zoom in on the
   * nearest station(s)
   */
  useEffect(() => {
    // Capture any changes to what is currently in the map view
    updateFilteredStationData();

    // Update the map pins to display the highest pollutants on top of the other marker pins
    setSortedMapPinData(sortByProperty(sortedStationData, "AirQualityCategorySortable"));

    /**
     * Only automatically pan and zoom the map if the
     * user hasn't been interacting with it
     */
    if (!userMapInteraction) {
      zoomToStations();
    }
  }, [sortedStationData]);

  /**
   * When the map lat/lng have changed update the relative
   * distances and sorting in the local-state map data
   */
  useEffect(() => {
    const originPoint = {
      "lat": 0,
      "lng": 0
    };

    if (mapCoords.lat === "0" || mapCoords.lng === "0") {
      // Use the user's current (or default) lat/lng values
      originPoint.lat = parseFloat(userLat);
      originPoint.lng = parseFloat(userLng);
    } else {
      // Use the map's current lat/lng values
      originPoint.lat = parseFloat(mapCoords.lat);
      originPoint.lng = parseFloat(mapCoords.lng);
    }

    if (stationData.length > 0) {
      // Capture any changes/updates to the station data
      setSortedStationData( addRelativeDistanceToStationData(originPoint, stationData) );
    }
  }, [mapCoords]);

  /**
   * When the map zoom level has changed, capture any new
   * stations that are now in view and add then to the
   * filtered data list
   */
  useEffect(() => {
    // Update the filtered data when the zoom level changes
    updateFilteredStationData();
  }, [currentZoomLevel]);

  /**
   * When the station data is updated, capture the new data
   * to update the relative distances and sorting
   */
  useEffect(() => {
    const originPoint = {
      "lat": 0,
      "lng": 0
    };

    if (mapCoords.lat === "0" || mapCoords.lng === "0") {
      // Use the user's current (or default) lat/lng values
      originPoint.lat = parseFloat(userLat);
      originPoint.lng = parseFloat(userLng);
    } else {
      // Use the map's current lat/lng values
      originPoint.lat = parseFloat(mapCoords.lat);
      originPoint.lng = parseFloat(mapCoords.lng);
    }

    if (stationData.length > 0) {
      // Capture any changes/updates to the station data
      setSortedStationData( addRelativeDistanceToStationData(originPoint, stationData) );
    }
  }, [stationData]);

  /**
   * Capture a reference to the Google Map component
   * so that we can access it later
   *
   * @param {Map} map The Map Object that was loaded
   *
   * @returns void
   */
  function handleGoogleMapLoad(map) {
    mapRef.current = map;

    zoomToStations();
  }

  /**
   * Update the local-state's center position of the map
   * so that the map doesn't revert to the
   * initial map position every time that it re-renders.
   *
   * @source https://stackoverflow.com/questions/61624547/can-i-set-a-default-center-value-in-react-googe-maps-api
   */
  function handleCenter() {
    if (!mapRef.current) return;
    const newPos = mapRef.current.getCenter().toJSON();

    userMapInteraction = true;

    // Update the local-state of the map position
    setMapCoords({
      "lat": newPos.lat.toString(),
      "lng": newPos.lng.toString()
    });
  }

  /**
   * Respond to changes to the map zoom level
   *
   * @returns void
   */
  function handleGoogleMapZoom() {
    let currentMapZoom = 0;

    if (!mapRef.current) return;

    currentMapZoom = mapRef.current.getZoom();

    // If the zoom level has changed, then set the local state to the current zoom
    if (currentZoomLevel !== currentMapZoom) {
      setCurrentZoomLevel(currentMapZoom);
    }
  }

  /**
   * Update the map zoom level and pan the viewport so
   * that several stations are visible at the same time
   *
   * @returns void
   */
  function zoomToStations() {
    let mapBounds = null;
    let stationDataByRegion = null;
    const visibleStationCount = googleMapMinimumVisibleStations;

    /**
     * No need for any adjustment if the zoom has already
     * happened, if the map hasn't been built yet or if
     * there are no stations to display
     */
    if (hasFitToBounds || !mapRef.current) {
      return;
    }

    // If there are no station data, set the map to the default zoom
    if (sortedStationData.length === 0) {
      mapRef.current.setZoom(googleMapDefaultZoom);
      return;
    }

    /**
     * If there is no user address and no region
     * to show by default, then show all the
     * stations in NSW
     */
    if (!userValidAddress && !regionName) {
      // Center the map on NSW (the default user position)
      mapRef.current.setCenter({
        lat: parseFloat(userLat),
        lng: parseFloat(userLng)
      });

      // Set the zoom level to the default
      mapRef.current.setZoom(googleMapDefaultZoom);
      return;
    }

    // Create a new boundary (from center of the world)
    mapBounds = new google.maps.LatLngBounds(); // eslint-disable-line no-undef

    /**
     * Retrieve the station data based from a specific region
     * if the region has been provided in the query string
     */
    if (!hasDisplayedRegion && regionName) {
      stationDataByRegion = filterArrayByProperty(sortedStationData, "Region", regionName)
    }

    /**
     * If the region data is to be displayed, then use
     * that to populate the map.
     * If no region ID has been provided, or if there
     * are no data for that region, then show stations
     * (up to the limit) based on their proximity to
     * the current location
     */
    if (!hasDisplayedRegion && regionName && stationDataByRegion.length > 0) {
      /**
       * Update the boundary with the co-ordinates of the
       * region stations to show
       */
      for (let b = 0, k = stationDataByRegion.length; b < k; ++b) {
        mapBounds.extend(
          {
            lat: parseFloat(stationDataByRegion[b].Latitude),
            lng: parseFloat(stationDataByRegion[b].Longitude)
          }
        );
      }

      /**
       * Set the flag that we have already displayed
       * the region stations
       */
      hasDisplayedRegion = true;
    } else {
      /**
       * Update the boundary with the co-ordinates of the
       * minimum number of stations to show
       */
      for (let i = 0; i < visibleStationCount; ++i) {
        mapBounds.extend(
          {
            lat: parseFloat(sortedStationData[i].Latitude),
            lng: parseFloat(sortedStationData[i].Longitude)
          }
        );
      }

      // Include the map position (user lat/lng) in the calculations
      mapBounds.extend(
        {
          lat: parseFloat(userLat),
          lng: parseFloat(userLng)
        }
      );
    }

    /**
     * Set the flag that we have already updated the boundaries
     * This is so we don't call this method repeatedly
     */
    hasFitToBounds = true;

    // Update the map to fit the new boundaries
    mapRef.current.fitBounds(mapBounds);

    /**
     * There is a bug with Google Maps where using
     * Restrictions can interfere with the fitBounds
     * method if you are zoomed out too far.
     * There is a point when zooming where the map forces
     * a zoom back in because otherwise you would see
     * outside of the resticted boundaries.
     *
     * More info: https://stackoverflow.com/questions/54712021/map-fitbounds-works-weird-when-map-has-restriction-option-set
     */
    mapRef.current.setCenter(mapBounds.getCenter());

    // Capture the position change in the local-state
    // This should automatically update the list of filtered data as well
    setMapCoords({
      "lat": mapBounds.getCenter().lat().toString(),
      "lng": mapBounds.getCenter().lng().toString()
    });
  }

  /**
   * Update the filtered list of station data to
   * be just what is currently visible in the map
   *
   * @returns void
   */
  function updateFilteredStationData() {
    if (sortedStationData.length > 0) {
      // Only set the filtered data if there is any station data to filter
      setFilteredStationDataFeed(
        {
          "currentMapZoom": currentZoomLevel,
          "visibleMarkers": getVisibleMarkers()
        }
      );
    }
  }

  /**
   * Check over each of the station's coordinates and
   * determine which ones are currently visible in the map
   *
   * @returns Array The list of stations that are currently visible in the map viewport
   */
  function getVisibleMarkers() {
     // Note: "sortedStationData" is a local state object
    const filteredStations = [];
    let markerIsInBounds = false;
    let mapBounds = null;

    // Capture the boundaries of the current map view
    mapBounds = mapRef?.current?.getBounds();

    // Check if the map boundaries are available yet
    if (mapBounds === undefined || mapBounds === null) {
      // if map bounds are not yet loaded, assume all stations are in view.
      // return all stations until map is interacted with
      return sortedStationData
    }

    for (let i = 0, j = sortedStationData.length; i < j; ++i) {
      // Calculate if the station lat/lng is in the current map view
      markerIsInBounds = mapBounds.contains({
        "lat": parseFloat(sortedStationData[i].Latitude),
        "lng": parseFloat(sortedStationData[i].Longitude)
      });

      if (markerIsInBounds) {
        filteredStations.push(sortedStationData[i]);
      }
    }

    return filteredStations;
  }

  /**
   * Respond to clicks on the Google Map.
   * The click event is not fired if a Marker or InfoWindow was clicked.
   *
   * @param {MapMouseEvent} mapMouseEvt The click Object returned by the Google map
   *
   * @returns void
   *
   * @source https://developers.google.com/maps/documentation/javascript/reference/map#MapMouseEvent
   */
  const onMapClick = () => {
    // If any popup is open, close it
    if (stationPopupContentId !== null) {
      setStationPopupContentId(null);
    }
  };

  /**
   * Respond to clicks on the station map markers
   *
   * @param {Click Event} evt Event that triggered the click
   * @param {String} stationSiteId The unique ID for the station that was clicked
   *
   * @returns void
   */
  const onStationMarkerClick = (evt, stationSiteId) => {
    /**
     * Get the content for this station (returned in an Array)
     * Note: "sortedStationData" is a local-state object
     */
    const markerContentSource = filterArrayByProperty(sortedStationData, "Site_Id", stationSiteId, 1)[0];
    const markerLatLng = {
      "lat": parseFloat(markerContentSource.Latitude),
      "lng": parseFloat(markerContentSource.Longitude)
    };

    evt.preventDefault();

    /**
     * Stop the click from reaching the Google Map
     * interface so that we can close the popup by
     * clicking on the map itself
     */
    evt.stopPropagation();

    userMapInteraction = true;

    if (mapRef.current) {
      mapRef.current.setCenter(markerLatLng);
    }

    /**
     * Capture the position change and move the map to
     * center on the station. This should automatically
     * update the list of filtered data as well
     */
    setMapCoords({
      "lat": markerContentSource.Latitude,
      "lng": markerContentSource.Longitude
    });

    // If the popup is already open (user has clicked the station twice), then close it
    if (stationPopupContentId !== null && stationPopupContentId === stationSiteId) {
      // Close the popup
      setStationPopupContentId(null);
    } else {
      /**
       * Open the new station popup.
       * Set the popup content in the local-state
       */
      setStationPopupContentId(stationSiteId);
    }
  };

  /**
   * Respond to clicks on the "map type" buttons.
   * Update the map to display a different type of map (e.g. roadmap vs hybrid)
   *
   * @param {Click Event} evt Event that triggered the click
   *
   * @returns void
   */
  const onToggleMapType = (evt) => {
    const mapTypeId = evt.currentTarget.getAttribute('data-type');

    evt.preventDefault();

    setMapLayerType(mapTypeId);

    if (mapRef.current) {
      mapRef.current.setMapTypeId(mapTypeId);

      if (mapTypeId === "hybrid") {
        mapRef.current.setOptions({
          styles: googleMapHybridStyles
        });
      } else {
        mapRef.current.setOptions({
          styles: googleMapRoadmapStyles
        });
      }
    }
  };

  /**
   * Respond to clicks on the "wind direction" button.
   * Update the map to display the wind direction icons
   * on the station markers
   *
   * @param {Click Event} evt Event that triggered the click
   *
   * @returns void
   */
  const onToggleStationWind = (evt) => {
    evt.preventDefault();

    if (windToggleIsActive) {
      setWindToggleActive(false);
    } else {
      setWindToggleActive(true);
    }
  };

  /**
   * Respond to clicks on the "pollutant popup" buttons.
   * Trigger the pollutant popup to open
   *
   * @param {Click Event} evt Event that triggered the click
   *
   * @returns void
   */
  const onPopupButtonClick = (evt) => {
    evt.preventDefault();
    // evt.stopPropagation();
    setPopupType(evt.currentTarget.getAttribute('data-popup-type'));
  };

  /**
   * Generate the React Component for the station popup
   *
   * @param {Object} station The station data
   *
   * @returns REACT OverlayView Component
   */
  function generatePopupComponent(station) {
    const stationUrl = "https://www.airquality.nsw.gov.au/";
    let stationPopupClasses = "station-map-view-overlay-popup__container has-status-";

    // Determine which colour scheme to use for the station
    switch (station.AirQualityCategory) {
      case "GOOD":
        stationPopupClasses += "good";
        break;

      case "FAIR":
        stationPopupClasses += "fair";
        break;

      case "POOR":
        stationPopupClasses += "poor";
        break;

      case "VERY POOR":
        stationPopupClasses += "very-poor";
        break;

      case "EXTREMELY POOR":
        stationPopupClasses += "extremely-poor";
        break;

        case "Inactive":
        stationPopupClasses += "inactive";
        break;

      default:
        break;
    }

    return (
      <OverlayView
        key="stationOverlay"
        position={
          {
            lat: parseFloat(station.Latitude),
            lng: parseFloat(station.Longitude)
          }
        }
        mapPaneName={OverlayView.FLOAT_PANE}
      >
        <div
          className={stationPopupClasses}
          ref={ ref => ref && google.maps.OverlayView.preventMapHitsFrom(ref) } // eslint-disable-line no-undef
          >
          <a href={ stationUrl + station.Site_Id } className="station-map-view-overlay-popup__summary">
            <div className="station-map-view-overlay-popup__details">
              <span className="station-map-view-overlay-popup__name">{station.SiteName}</span>
              <span className="station-map-view-overlay-popup__status">{station.AirQualityCategory}</span>
            </div>
            <div className="station-map-view-overlay-popup__icon">
              <ARROW_RIGHT />
            </div>
          </a>
          {
            (station.DeterminingPollutantValue !== null) ?
              <div className="station-map-view-overlay-popup__data-wrapper">
                <div className="station-map-view-overlay-popup__data">
                  <div className="station-map-view-overlay-popup__data-details">
                    <button onClick={onPopupButtonClick} data-popup-type={station.DeterminingPollutant} className="station-map-view-overlay-popup__data-unit station-map-view-overlay-popup__data-button" aria-haspopup="dialog">
                      {
                        (station.DeterminingPollutant.toLowerCase() !== "ozone") ?
                          `Particles (${station.DeterminingPollutant})`
                        : station.DeterminingPollutant
                      }
                    </button>
                  </div>
                  <div className="station-map-view-overlay-popup__data-value">
                    { standardizeValue(station.DeterminingPollutantValue) } {station.DeterminingPollutantUnit}
                  </div>
                </div>
              </div>
            : null
          }
          {
            (windToggleIsActive && station.WindDirectionDegrees !== null) ? (
              <div className="station-map-view-overlay-popup__wind-wrapper">
                <div className="station-map-view-overlay-popup__data">
                  <div className="station-map-view-overlay-popup__data-details">
                    <div className="station-map-view-overlay-popup__data-unit">
                      Wind
                    </div>
                  </div>
                  <div className="station-map-view-overlay-popup__data-value">
                    {station.WindDirectionCompass} {station.WindDirectionDegrees}&deg; { (station.WindSpeed !== null ? `${ standardizeValue(station.WindSpeed) }m/s` : null) }
                  </div>
                </div>
              </div>
            ) : null
          }
        </div>
      </OverlayView>
    )
  }

  return (
    <div className={(isHidden) ? 'station-map-view__section' : 'station-map-view__section is-visible'}>
      <div className={ (mapLayerType === "hybrid") ? 'station-map-view__container station-map__satellite-view' : 'station-map-view__container' }>
        <LoadScript
          googleMapsApiKey={mapConfig.mapsApiKey}
          loadingElement={loadingMessage} >
          <GoogleMap
            mapContainerClassName="station-map__container"
            mapContainerStyle={googleMapContainerStyle}
            onClick={onMapClick}
            onDragEnd={handleCenter}
            onLoad={handleGoogleMapLoad}
            onZoomChanged={handleGoogleMapZoom}
            options={googleMapOptions}
            zoom={currentZoomLevel}
            >
            {
              (sortedMapPinData.length !== 0) ? sortedMapPinData.map( station => {
                const windDirectionStyles = {
                  "transform": ""
                };
                const airQualityRating = station.AirQualityCategory;
                const regionContribution = station.ContributeToNewRegionalAQC;
                let iconPath = "";
                let labelClasses = "station-map-view-overlay__label";
                let windClasses = "station-map-view-overlay__wind-icon";
                let iconClasses = "station-map-view-overlay__map-icon";

                // Show the station label if the zoom level requirement has been met
                if (currentZoomLevel >= googleMapLabelUnlockZoom) {
                  labelClasses += " is-visible";
                }

                // Allow the station wind icon to be toggled if the zoom level requirement has been met
                if (currentZoomLevel >= googleMapWindUnlockZoom) {
                  windClasses += " is-unlocked";
                }

                // Show the station wind icon if it has been toggled on
                if (windToggleIsActive && currentZoomLevel >= googleMapLabelUnlockZoom) {
                  windClasses += " is-visible";

                  /**
                   * Set the style that shows the wind direction
                   * Note: The orientation for the wind direction is effectively
                   * the opposite of a compass, so we flip the direction
                   */
                  if (station.WindDirectionDegrees !== null) {
                    windDirectionStyles.transform = "rotate(" + (station.WindDirectionDegrees + 180) + "deg)";
                  }
                }

                // Determine which icon to use for the marker
                switch (true) {
                  case (airQualityRating === "GOOD" && regionContribution !== null):
                    iconPath = mapConfig.mapIcons.good;
                    break;

                  case (airQualityRating === "GOOD" && regionContribution === null):
                    iconPath = mapConfig.mapIcons.goodExcluded;
                    iconClasses += " icon-excluded";
                    break;

                  case (airQualityRating === "FAIR" && regionContribution !== null):
                    iconPath = mapConfig.mapIcons.fair;
                    break;

                  case (airQualityRating === "FAIR" && regionContribution === null):
                    iconPath = mapConfig.mapIcons.fairExcluded;
                    iconClasses += " icon-excluded";
                    break;

                  case (airQualityRating === "POOR" && regionContribution !== null):
                    iconPath = mapConfig.mapIcons.poor;
                    break;

                  case (airQualityRating === "POOR" && regionContribution === null):
                    iconPath = mapConfig.mapIcons.poorExcluded;
                    iconClasses += " icon-excluded";
                    break;

                  case (airQualityRating === "VERY POOR" && regionContribution !== null):
                    iconPath = mapConfig.mapIcons.veryPoor;
                    break;

                  case (airQualityRating === "VERY POOR" && regionContribution === null):
                    iconPath = mapConfig.mapIcons.veryPoorExcluded;
                    iconClasses += " icon-excluded";
                    break;

                  case (airQualityRating === "EXTREMELY POOR" && regionContribution !== null):
                    iconPath = mapConfig.mapIcons.extremelyPoor;
                    break;

                  case (airQualityRating === "EXTREMELY POOR" && regionContribution === null):
                    iconPath = mapConfig.mapIcons.extremelyPoorExcluded;
                    iconClasses += " icon-excluded";
                    break;

                  case (airQualityRating === "Inactive" && regionContribution !== null):
                    iconPath = mapConfig.mapIcons.inactive;
                    break;

                  case (airQualityRating === "Inactive" && regionContribution === null):
                    iconPath = mapConfig.mapIcons.inactiveExcluded;
                    iconClasses += " icon-excluded";
                    break;

                  default:
                    break;
                }

                return (
                  <OverlayView
                    key={station.id}
                    position={
                      {
                        lat: parseFloat(station.Latitude),
                        lng: parseFloat(station.Longitude)
                      }
                    }
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <div className="station-map-view-overlay__container">
                      {
                        // Show wind data for stations that have it
                        (station.WindDirectionDegrees !== null) ?
                          <div className={windClasses} style={windDirectionStyles}>
                            <ARROW_COMPASS />
                          </div>
                        : null
                      }
                      <span className={labelClasses}>{station.SiteName}</span>
                      <button
                        onClick={
                          (evt) => {
                            onStationMarkerClick(evt, station.Site_Id)
                          }
                        }
                        className="station-map-view-overlay__button"
                        aria-haspopup="dialog"
                        >
                        <span className="visuallyhidden">More information from {station.SiteName}</span>
                        <img src={iconPath} className={iconClasses} alt="" role="presentation" />
                      </button>
                    </div>
                  </OverlayView>
                );
              }) : null
            }
            {
              (stationPopupContentId !== null) ? generatePopupComponent( filterArrayByProperty(sortedStationData, "Site_Id", stationPopupContentId, 1)[0] ): null
            }
          </GoogleMap>
        </LoadScript>

        <div className="station-map-view-overlay__container">
          <div id="wind-toggle" className="station-map-view-wind-toggle__container">
            <button onClick={onToggleStationWind} className={ (windToggleIsActive && currentZoomLevel >= googleMapLabelUnlockZoom) ? 'station-map-view-wind-toggle__button is-active' : 'station-map-view-wind-toggle__button' } disabled={ (currentZoomLevel < googleMapLabelUnlockZoom) ? true : false } >
              <span className="station-map-view-wind-toggle__checkbox">
                <span className="station-map-view-wind-toggle__icon">
                  <TICK />
                </span>
              </span>
              <span className="station-map-view-wind-toggle__label-wrapper">
                <span className="station-map-view-wind-toggle__label">
                  <span className="visuallyhidden">Toggle </span>Wind direction
                </span>
                <span className="station-map-view-wind-toggle__message">
                  Zoom in to view
                </span>
              </span>
            </button>
          </div>

          <button className={ (mapLayerType === "roadmap") ? 'station-map-view-toggle__button is-visible' : 'station-map-view-toggle__button' } onClick={onToggleMapType} data-type="hybrid">
            <span className="visuallyhidden">Toggle map hybrid view</span>
            <img src={mapConfig.mapIcons.satellite} className="station-map-view-toggle__image" alt="" role="presentation" />
          </button>

          <button className={ (mapLayerType === "hybrid") ? 'station-map-view-toggle__button is-visible' : 'station-map-view-toggle__button' } onClick={onToggleMapType} data-type="roadmap">
            <span className="visuallyhidden">Toggle map road view</span>
            <img src={mapConfig.mapIcons.road} className="station-map-view-toggle__image" alt="" role="presentation" />
          </button>

          <ListView
            isOverlay={true}
            textContent={mapConfig.textContent}
          />

          <div className="station-map-skip-links" aria-hidden="true">
            <nav className="station-map-skip-links__list">
              <a className="station-map-skip-links__link" href="#search-filters">Return to search and filters</a>
            </nav>
          </div>
        </div>
      </div>

      <MapKey />
    </div>
  );
};

MapView.propTypes = {
  "mapConfig": PropTypes.object,
  "stationData": PropTypes.array,
  "setFilteredStationDataFeed": PropTypes.oneOfType( [PropTypes.array, PropTypes.func] ),
  "userValidAddress": PropTypes.bool,
  "userLat": PropTypes.string,
  "userLng": PropTypes.string,
  "windToggleIsActive": PropTypes.bool,
  "setWindToggleActive": PropTypes.oneOfType( [PropTypes.bool, PropTypes.func] ),
  "setPopupType": PropTypes.oneOfType( [PropTypes.string, PropTypes.func] ),
  "uniqueMapId": PropTypes.string,
  "isHidden": PropTypes.bool
};

const mapStateToProps = (state) => {
  return {
    "stationData": selectStationData(state),
    "userValidAddress": selectUserValidAddress(state),
    "userLat": selectUserLat(state),
    "userLng": selectUserLng(state),
    "windToggleIsActive": selectWindToggleIsActive(state),
    "uniqueMapId": selectUniqueMapId(state)
  }
};

const mapDispatchToProps = (dispatch) => {
  return {
    "setFilteredStationDataFeed": stationData => dispatch(setFilteredStationData(stationData)),
    "setWindToggleActive": isActive => dispatch(setWindToggleActive(isActive)),
    "setPopupType": popupTypeId => dispatch(setPollutantPopupType(popupTypeId))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(React.memo(MapView));
